const APP_TITLE = 'Statura Care';

const SEGMENT_LABELS: Record<string, string> = {
  acp: 'Advance Care Plan',
  admin: 'Admin',
  'advance-care-plans': 'Advance Care Plans',
  'ai-usage': 'AI Usage',
  'at-hm': 'AT-HM',
  auth: 'Auth',
  availability: 'Availability',
  billing: 'Billing',
  budgets: 'Budgets',
  capabilities: 'Capabilities',
  'care-pathways': 'Care Pathways',
  'care-plans': 'Care Plans',
  claims: 'Claims',
  clients: 'Clients',
  'cm-task-types': 'CM Task Types',
  compliance: 'Compliance',
  configuration: 'Configuration',
  contractors: 'Contractors',
  dashboard: 'Dashboard',
  devices: 'Devices',
  documents: 'Documents',
  'document-templates': 'Document Templates',
  exceptions: 'Exceptions',
  family: 'Family Portal',
  finance: 'Finance',
  handovers: 'Handovers',
  'home-care': 'Home Care',
  hr: 'HR',
  invoices: 'Invoices',
  leave: 'Leave',
  live: 'Live',
  instances: 'Workflow Runs',
  mar: 'MAR',
  medications: 'Medications',
  modules: 'Modules',
  monitoring: 'Monitoring',
  notifications: 'Notifications',
  onboarding: 'Onboarding',
  'onboarding-packs': 'Onboarding Packs',
  'open-shifts': 'Open Shifts',
  payroll: 'Payroll',
  periods: 'Periods',
  policies: 'Policies',
  portal: 'Portal',
  pricing: 'Pricing',
  profile: 'Profile',
  quality: 'Quality',
  reconciliation: 'Reconciliation',
  regions: 'Regions',
  reports: 'Reports',
  residential: 'Residential',
  roster: 'Roster',
  sah: 'SAH',
  schedules: 'Schedules',
  security: 'Security',
  settings: 'Settings',
  'service-areas': 'Service Areas',
  'service-delivery': 'Service Delivery',
  'signature-templates': 'Signature Templates',
  signatures: 'Documents and Signatures',
  staff: 'Staff',
  statements: 'Statements',
  subscriptions: 'Subscriptions',
  system: 'System',
  tasks: 'Tasks',
  telehealth: 'Telehealth',
  templates: 'Templates',
  timesheets: 'Timesheets',
  training: 'Training',
  users: 'Users',
  workflows: 'Workflows',
  workforce: 'Workforce',
  wounds: 'Wounds',
};

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Home',
  '/overview': 'Home',
  '/activity': 'Activity',
  '/clients/new': 'New Client',
  '/home-care/classifications': 'Home Care Classifications',
  '/compliance/quality/qi-data-entry/submit': 'AIHW QI Submission',
  '/clients/[id]/clinical/thresholds': 'Observation Thresholds',
  '/home-care/service-delivery': 'Service Delivery',
  '/medications/reconciliation/new': 'Medication Reconciliation',
  '/home-care/budgets': 'Home Care Budgets',
  '/home-care/funding-changes': 'Client Funding Changes',
  '/home-care/claims': 'Home Care Claims',
  '/home-care/statements': 'Home Care Statements',
  '/compliance/sah-evidence': 'SAH Evidence Oversight',
  '/medications/mar': 'Medication Administration Record',
  '/roster/open-shifts': 'Open Shifts',
  '/workflows/instances': 'Workflow Runs',
};

function isLikelyId(segment: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
    /^[0-9a-f]{20,}$/i.test(segment) ||
    /^\d+$/.test(segment)
  );
}

function titleCase(segment: string): string {
  return segment
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * The human label for a route, with no app-title suffix — e.g. "Roster - Periods".
 * Dynamic segments are dropped or named (see isLikelyId below), so a raw UUID
 * never reaches user-facing copy.
 */
export function pageLabelFromPathname(pathname: string | null | undefined): string {
  const rawPath = (pathname || '/').split('?')[0] ?? '/';
  const path = rawPath.replace(/\/+$/, '') || '/';
  const exact = ROUTE_LABELS[path];
  if (exact) return exact;

  const labels = path
    .split('/')
    .filter(Boolean)
    .map((segment, index, segments) => {
      if (isLikelyId(segment)) {
        const previous = segments[index - 1];
        if (previous === 'clients') return 'Client Details';
        if (previous === 'staff') return 'Staff Details';
        if (previous === 'tasks') return 'Task Details';
        if (previous === 'instances') return 'Workflow Run';
        if (previous === 'care-plans') return 'Care Plan';
        if (previous === 'wounds') return 'Wound Details';
        return null;
      }
      return SEGMENT_LABELS[segment] ?? titleCase(segment);
    })
    .filter((label): label is string => Boolean(label));

  const meaningful = labels.slice(-3);
  return meaningful.length > 0 ? meaningful.join(' - ') : 'Dashboard';
}

export function pageTitleFromPathname(pathname: string | null | undefined): string {
  const rawPath = (pathname || '/').split('?')[0] ?? '/';
  const path = rawPath.replace(/\/+$/, '') || '/';
  const exact = ROUTE_LABELS[path];
  if (exact) return `${exact} | ${APP_TITLE}`;
  return `${pageLabelFromPathname(pathname)} | ${APP_TITLE}`;
}
